import { createApi } from '@reduxjs/toolkit/query/react';
import { User, UserWithCompany, UserWithToken } from '@typeRoots/common/user';
import { setCurrentUser, setUser, setProducerFormId } from '../../user/slice/userSlice';
import { baseQueryWithProgress } from 'src/helpers/fetchQueryHelper';
import { setAccountType } from 'src/slices/UiSlice';

export interface ProducerAvailabilityRequest {
  token: string;
  garagingState?: string;
}

export interface ProducerAvailabilityResponse {
  destination: 'tpi' | 'tivly';
  producerEmail: string | null;
  redirectUrl: string | null;
}

export const authenticationApi = createApi({
  reducerPath: 'authenticationApi',
  tagTypes: ['User'],
  baseQuery: baseQueryWithProgress,

  endpoints: (builder) => ({
    //send jwt to get user data
    verifyUserWithJwt: builder.query<UserWithCompany, string>({
      query: (jwt) => ({
        url: `/auth/verify-jwt?token=${jwt}`, // Fetch user data
        method: 'POST',
      }),

      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          const { data: userInfo } = await queryFulfilled;
          const { user, accountType, producerFormId } = userInfo;
          dispatch(setUser(user)); // add this user in users array
          dispatch(setCurrentUser(user)); // set this user as current user
          dispatch(setAccountType(accountType)); // set the account type
          if (producerFormId) {
            dispatch(setProducerFormId(producerFormId)); // set the producer form ID
          }
        } catch (error) {
          console.log('Error getting users from jwt', error);
        }
      },
      // Tagging vehicles to automatically refresh this cache when needed
      providesTags: ['User'],
    }),

    //register new user
    registerUser: builder.mutation<UserWithToken, Partial<User>>({
      query: (newUser) => ({
        url: 'auth/register',
        method: 'POST',
        body: newUser,
      }),
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          const { data: userData } = await queryFulfilled;
          dispatch(setUser(userData.user)); // add this user in users array
          dispatch(setCurrentUser(userData.user)); // set this user as current user
        } catch (error) {
          console.log('Error creating new user', error);
        }
      },
    }),

    // New endpoint for getting redirect URL
    getRedirectUrl: builder.mutation<
      { redirectUrl: string },
      { newUser: Partial<User>; token: string; dotNumber: string }
    >({
      query: (body) => ({
        url: '/auth/get-redirect-url',
        method: 'POST',
        body,
      }),
    }),

    // Check producer availability based on garaging state
    checkProducerAvailability: builder.mutation<
      ProducerAvailabilityResponse,
      ProducerAvailabilityRequest
    >({
      query: (body) => ({
        url: '/auth/producer-availability',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useRegisterUserMutation,
  useVerifyUserWithJwtQuery,
  useLazyVerifyUserWithJwtQuery,
  useGetRedirectUrlMutation,
  useCheckProducerAvailabilityMutation,
} = authenticationApi;
